import {useQuery, useInfiniteQuery, useQueryClient, useMutation} from "react-query";
import {toast} from 'react-toastify';
import API from './utils/api';
import { CreateOrUpdateOrganizationConfigDto, OrganizationConfig } from "../models/organization-config.dto";
import ENDPOINTS from "./utils/endpoints";
import useUserStore from "../state-management/useUserStore";

export function useGetOrganizationConfigById(organizationId: string) {
    const url = ENDPOINTS.ORGANIZATION.ORGANIZATION_CONFIG.DETAIL;
    const request = API.OrganizationAPI.getOrganizationConfigById(organizationId);

    const {data, isLoading, error} = useQuery<{ data: OrganizationConfig }, Error>(
        [url, organizationId],
        (() => request),
        {
            enabled: !!(organizationId),
        }
    );

    return {
        data: data,
        isLoading,
        error,
    };
}
/**
 * Reloads the signed-in user's organization config (subscription limits such as
 * maxCompanies) into the user store, e.g. after a subscription change is paid for.
 */
export function useRefreshOwnOrganizationConfig() {
    const { currentUser, setCurrentUser } = useUserStore();

    return async () => {
        const organization = currentUser.user.organization;
        if (!organization?.id) return;

        try {
            const response = await API.OrganizationAPI.getOrganizationConfigById(organization.id);
            const latestUser = useUserStore.getState().currentUser;
            setCurrentUser({
                ...latestUser,
                user: {
                    ...latestUser.user,
                    organization: { ...latestUser.user.organization!, config: response.data },
                },
            });
        } catch {
            // Keep the existing config; it is refreshed again on the next sign in
        }
    };
}

export function useCreateOrUpdateOrganizationConfig() {
    const queryClient = useQueryClient();

    return useMutation(
        ({payload}: {payload: CreateOrUpdateOrganizationConfigDto}) => API.OrganizationAPI.createOrUpdateOrganizationConfig(payload),
        {
            onSuccess: () => {
                toast.success('Organization configurations updated successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: () => {
                
            },
        }
    )
}
